import type { DataProvider, Identifier } from "ra-core";
import type {
  CampaignTarget,
  CampaignTargetDraft,
  CampaignTaskTarget,
} from "../types";

export type { CampaignTargetDraft };

export function targetDraftKey(target: CampaignTargetDraft): string {
  return `${target.target_type}-${target.bc_segment_id ?? ""}-${target.company_id ?? ""}-${target.contact_id ?? ""}-${target.tag_id ?? ""}`;
}

export function toTargetDraft(
  target: CampaignTarget | CampaignTaskTarget,
): CampaignTargetDraft {
  return {
    target_type: target.target_type,
    bc_segment_id: target.bc_segment_id ?? null,
    company_id: target.company_id ?? null,
    contact_id: target.contact_id ?? null,
    tag_id: target.tag_id ?? null,
  };
}

export async function loadCampaignTargets(
  dataProvider: DataProvider,
  campaignId: Identifier,
): Promise<CampaignTargetDraft[]> {
  const { data } = await dataProvider.getList<CampaignTarget>("campaign_targets", {
    filter: { campaign_id: campaignId },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "id", order: "ASC" },
  });
  return data.map(toTargetDraft);
}

export async function replaceCampaignTargets(
  dataProvider: DataProvider,
  campaignId: Identifier,
  targets: CampaignTargetDraft[],
): Promise<void> {
  const { data: existing } = await dataProvider.getList<CampaignTarget>(
    "campaign_targets",
    {
      filter: { campaign_id: campaignId },
      pagination: { page: 1, perPage: 500 },
    },
  );

  await Promise.all(
    existing.map((row) =>
      dataProvider.delete("campaign_targets", {
        id: row.id,
        previousData: row,
      }),
    ),
  );

  await Promise.all(
    targets.map((target) =>
      dataProvider.create("campaign_targets", {
        data: { ...target, campaign_id: campaignId },
      }),
    ),
  );
}

export async function replaceDefinitionTargets(
  dataProvider: DataProvider,
  definitionId: Identifier,
  targets: CampaignTargetDraft[],
): Promise<void> {
  const { data: existing } = await dataProvider.getList<CampaignTaskTarget>(
    "campaign_task_targets",
    {
      filter: { campaign_task_definition_id: definitionId },
      pagination: { page: 1, perPage: 500 },
    },
  );

  await Promise.all(
    existing.map((row) =>
      dataProvider.delete("campaign_task_targets", {
        id: row.id,
        previousData: row,
      }),
    ),
  );

  await Promise.all(
    targets.map((target) =>
      dataProvider.create("campaign_task_targets", {
        data: { ...target, campaign_task_definition_id: definitionId },
      }),
    ),
  );
}

export async function clearDefinitionTargets(
  dataProvider: DataProvider,
  definitionId: Identifier,
): Promise<void> {
  const { data: existing } = await dataProvider.getList<CampaignTaskTarget>(
    "campaign_task_targets",
    {
      filter: { campaign_task_definition_id: definitionId },
      pagination: { page: 1, perPage: 500 },
    },
  );

  await Promise.all(
    existing.map((row) =>
      dataProvider.delete("campaign_task_targets", {
        id: row.id,
        previousData: row,
      }),
    ),
  );
}

export function assertCreatedId(
  record: { id?: Identifier } | null | undefined,
  resource: string,
): Identifier {
  if (record?.id == null) {
    throw new Error(`Failed to create ${resource}: missing id in response`);
  }
  return record.id;
}
